import MainLayout from "@/lib/layout/MainLayout";
import { createFileRoute } from "@tanstack/react-router";
import { Dot } from "lucide-react";

export const Route = createFileRoute("/_dashboard/privacy")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <MainLayout.Header headline="Privacy Policy"></MainLayout.Header>
      <MainLayout.Content>
        {/* --- Quick Summary --- */}

        <div>
          <h2 className="text-2xl font-semibold">2-Minute Summary</h2>
          <ul className="space-y-3 py-2">
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Control of Data</strong>: You remain in control of your
                data at all times.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Applicability</strong>: This Privacy Policy applies to
                all users of <span className="font-semibold">wouldu.io</span>.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Data Collected</strong>: We collect account details and
                optional demographic data (gender and nationality) used
                exclusively for poll statistics.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Purpose</strong>: We use this data to provide the
                service, improve polls, and keep the platform safe.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Access</strong>: Only the site operator and trusted
                service providers (e.g., analytics services) have access under
                strict confidentiality.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Your Rights</strong>: You can modify or delete your
                data, or request a copy of it at any time.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <Dot className="text-primary mt-1 h-4 w-4 shrink-0" />
              <p>
                <strong>Security</strong>: Your data is stored securely and
                protected against unauthorized access.
              </p>
            </li>
          </ul>
          <p className="text-muted-foreground">
            For more details, continue reading the full Privacy Policy below.
          </p>
        </div>

        {/* --- Full Privacy Policy --- */}
        <div>
          <h2 className="text-2xl font-semibold">Full Privacy Policy</h2>

          {/* 1. General */}
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">1. General</h3>
            <p>
              The website <span className="font-semibold">wouldu.io</span>{" "}
              (hereinafter “we,” “us,” or “our”) is operated by a private
              individual (site owner). For any inquiries, please contact us at{" "}
              <a
                href="mailto:contact@wouldu.io"
                className="text-primary-wu hover:underline"
              >
                contact@wouldu.io
              </a>
              .
            </p>
            <p>
              This Privacy Policy explains how we collect, use, and protect your
              personal data in compliance with applicable data protection laws,
              including the GDPR.
            </p>
          </div>
        </div>

        {/* 2. Data We Collect */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">2. Data We Collect</h3>

          <h4 className="font-semibold">2.1 Categories of Personal Data</h4>
          <ul className="list-inside list-disc space-y-1">
            <li>
              <strong>Category 1: Account information</strong> (e.g., username,
              email address, hashed password).
            </li>
            <li>
              <strong>Category 2: Optional demographic data</strong> (gender and
              nationality). This information is{" "}
              <span className="italic">only</span> used in aggregated form for
              poll statistics.
            </li>
            <li>
              <strong>Category 3: Tool usage data</strong> (e.g., polls created,
              votes, comments, and general activity).
            </li>
          </ul>
        </div>

        {/* 3. Purpose and Legal Basis */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">3. Purpose and Legal Basis</h3>

          <h4 className="font-semibold">3.1 General Purposes</h4>
          <div className="overflow-x-auto">
            <table className="border-border w-full border text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="border px-3 py-2 text-left">Data Category</th>
                  <th className="border px-3 py-2 text-left">Legal Basis</th>
                  <th className="border px-3 py-2 text-left">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border px-3 py-2">Category 1</td>
                  <td className="border px-3 py-2">
                    Performance of a contract
                  </td>
                  <td className="border px-3 py-2">
                    To create and manage your account.
                  </td>
                </tr>
                <tr>
                  <td className="border px-3 py-2">Category 2</td>
                  <td className="border px-3 py-2">Consent</td>
                  <td className="border px-3 py-2">
                    To generate anonymous poll statistics.
                  </td>
                </tr>
                <tr>
                  <td className="border px-3 py-2">Category 3</td>
                  <td className="border px-3 py-2">Legitimate interest</td>
                  <td className="border px-3 py-2">
                    To improve services, provide support, and ensure platform
                    security.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-semibold">3.2 Sharing Data with Third Parties</h4>
          <p>
            We do not sell or rent your personal data. However, we may share it
            with trusted service providers (e.g., hosting services){" "}
            {/* <span className="font-semibold">Google Analytics</span>) under
            strict confidentiality agreements. */}
          </p>
        </div>

        {/* 4. Retention */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">4. Data Retention</h3>
          <ul className="list-inside list-disc space-y-1">
            <li>
              <strong>Category 1</strong>: Deleted within 90 days after account
              deletion.
            </li>
            <li>
              <strong>Category 2</strong>: Deleted immediately upon account
              deletion.
            </li>
            <li>
              <strong>Category 3</strong>: Retained for up to 2 years after
              account deletion to maintain platform security and integrity.
            </li>
          </ul>
        </div>

        {/* 5. Rights */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">5. Your Rights</h3>
          <p>
            Under GDPR, you have the right to access, correct, delete, restrict,
            or object to the processing of your personal data. You may also
            request data portability and withdraw consent at any time.
          </p>
        </div>

        {/* 6. Security */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">6. Security</h3>
          <p>
            We implement technical and organizational measures to protect your
            personal data. While no system is completely secure, we will notify
            you promptly of any data breach and provide guidance on steps you
            can take to protect yourself.
          </p>
        </div>

        {/* 7. Changes */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">7. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. Significant
            changes will be communicated via email or directly on the platform.
          </p>
        </div>

        {/* 8. Contact */}
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">8. Contact</h3>
          <p>
            For questions or concerns regarding this Privacy Policy, you can
            reach us at:
          </p>
          <ul className="list-inside list-disc">
            <li>Email: contact@wouldu.io</li>
          </ul>
        </div>
      </MainLayout.Content>
    </>
  );
}
